import EditIcon from "@mui/icons-material/Edit";
import { Box, Button, Grid, Typography, useTheme } from "@mui/material";

import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";

export interface InformationItem {
  label: string;
  value: React.ReactNode;
  size?: 6 | 12;
}

export interface InformationSection {
  title?: string;
  items: InformationItem[];
}

interface InformationViewProps {
  title: string;
  onEdit?: () => void;
  editLabel?: string;
  sections: InformationSection[];
}

const InfoItem = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): React.ReactNode => {
  const theme = useTheme();

  return (
    <Box sx={{ padding: Spacing.XS }}>
      <Typography
        sx={{
          fontSize: "14px",
          color: theme.palette.text.secondary,
          fontWeight: 400,
          marginBottom: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        component="div"
        sx={{
          fontSize: "16px",
          color: theme.palette.text.primary,
          fontWeight: 400,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export const InformationView = ({
  title,
  onEdit,
  editLabel,
  sections,
}: InformationViewProps): React.ReactNode => {
  const theme = useTheme();
  const { t } = useResource();

  const visibleSections = sections
    .map((section, originalIndex) => ({
      ...section,
      originalIndex,
      items: section.items.filter(
        (item) =>
          item.value !== null && item.value !== undefined && item.value !== "",
      ),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: Spacing.M,
        }}
      >
        <Typography
          variant="h6"
          sx={{ color: theme.palette.text.primary, fontWeight: 600 }}
        >
          {title}
        </Typography>
        {onEdit && (
          <Button
            variant="text"
            color="primary"
            startIcon={<EditIcon />}
            onClick={onEdit}
            sx={{ fontSize: "16px", textTransform: "none" }}
          >
            {editLabel ?? t(Resources.Common.Edit)}
          </Button>
        )}
      </Box>
      <Box
        sx={{
          backgroundColor: theme.colors.backgroundTile,
          borderRadius: "8px",
          padding: Spacing.M,
        }}
      >
        {visibleSections.map((section, index) => (
          <Box
            key={section.originalIndex}
            sx={index > 0 ? { mt: Spacing.M } : {}}
          >
            {section.title && (
              <Box sx={{ mb: Spacing.M }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                  }}
                >
                  {section.title}
                </Typography>
              </Box>
            )}
            <Grid container spacing={Spacing.M}>
              {section.items.map((item) => (
                <Grid key={item.label} size={item.size ?? 6}>
                  <InfoItem label={item.label} value={item.value} />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
